import Head from 'expo-router/head';
import { useState } from 'react';
import { ScrollView } from 'react-native';
import { getTokenValue, Text, YStack } from 'tamagui';

import PSOtpInput from '@/components/ui/PSOtpInput';

/**
 * ── /components/ps-otp-input ─────────────────────────────────────────────
 *
 * PSOtpInput's real call sites are auth's verify-mobile.tsx and
 * verify-email.tsx — both wire it identically: `autoFocus`, `onFocus`
 * clearing the screen's own `error`, `onComplete` kicking off the verify
 * request, and `disabled` while that request is in flight. What's worth
 * previewing isn't a grid of static variants (there's only one visual family
 * — bordered box, active-cell border, all-red error) but the STATEFUL
 * behaviour the component's docstring calls out: it's one hidden real
 * `Input` over six presentational boxes (not six inputs), completeness is
 * the atom's own fact while correctness is the screen's, and focus clears
 * BOTH errors while blur-while-incomplete sets the atom's own. Each block
 * below exercises one of those, and cites the real screen behaviour it
 * mirrors.
 * ─────────────────────────────────────────────────────────────────────────
 */

export default function PSOtpInputPreview() {
  // Drives the primary live demo: dual error ownership + onComplete. Typing
  // a fake "wrong" code (anything but 000000) surfaces the SCREEN's error
  // exactly like a rejected verify would; typing 000000 "succeeds" and
  // clears it. Tapping back into the field clears the error again (real
  // screens do this via `onFocus`), and tabbing/pressing away while
  // partially filled surfaces the ATOM's own "incomplete" error — the atom
  // needs no wiring for that part, it's automatic.
  const [otp, setOtp] = useState('');
  const [otpError, setOtpError] = useState<string | undefined>();

  // A second, independent field for the `autoFocus` block below — kept
  // separate so its imperative focus-on-screen-focus doesn't fight the
  // primary demo's field for the keyboard.
  const [autoFocusOtp, setAutoFocusOtp] = useState('');

  return (
    <>
      <Head>
        <title>PSOtpInput · Putting Scene</title>
      </Head>
      <ScrollView
        style={{
          flex: 1,
          backgroundColor: getTokenValue('$color.background', 'color'),
        }}
      >
        <YStack items="flex-start" gap="$lg" p="$lg">
          {/* live — tap a box to focus (highlights the next-to-fill cell in
            $brand), type a 6-digit code. Enter "000000" to see onComplete
            "succeed" (error clears); enter anything else to see it "fail"
            (screen sets `error`, every box turns $error red). Re-focusing
            the field clears that error via `onFocus`, same as real
            verify — the screen doesn't wait for a fresh keystroke. Blur
            with a partial code in (e.g. type 3 digits, tap outside) to see
            the ATOM's own "Please enter the complete code" — that one needs
            no wiring here, PSOtpInput sets it itself on blur-while-
            incomplete. */}
          <YStack gap="$xs">
            <Text fontFamily="$body" fontSize="$small" color="$textDarkMuted">
              live — type &quot;000000&quot; to succeed, anything else to see
              the screen&apos;s error; focus clears it; blur with a partial code
              shows the atom&apos;s own incomplete error
            </Text>
            <PSOtpInput
              value={otp}
              onChangeText={setOtp}
              error={otpError}
              onFocus={() => setOtpError(undefined)}
              // Verify the `code` ARGUMENT, not `otp` state — onComplete
              // fires in the same tick as onChangeText, so `otp` hasn't
              // flushed yet (see the atom's docstring). Both real screens
              // verify this way against the server; this fakes that check.
              onComplete={(code) =>
                setOtpError(
                  code === '000000' ? undefined : 'Incorrect code, try again'
                )
              }
            />
          </YStack>

          {/* error — the screen-owned state: a rejected verify. All six
            boxes turn $error red (not just the active one) and the message
            prints below, no matter how many digits are filled. This is a
            static instance (not wired to onChangeText) purely to hold the
            look still for reference, next to the live block above which
            reaches the same visual through actual typing. */}
          <YStack gap="$xs">
            <Text fontFamily="$body" fontSize="$small" color="$textDarkMuted">
              error — screen-owned (a rejected code), every box red; takes
              precedence over the atom&apos;s own incomplete error
            </Text>
            <PSOtpInput
              value="123"
              onChangeText={() => {}}
              error="Incorrect code, try again"
            />
          </YStack>

          {/* disabled — both real screens pass `disabled={verifying}` so a
            second tap can't re-fire the verify request while the first is
            still in flight. Dims the whole row to 0.4 opacity and disables
            the hidden input; shown here mid-code, the way it'd look the
            instant onComplete fires and the screen starts verifying. */}
          <YStack gap="$xs">
            <Text fontFamily="$body" fontSize="$small" color="$textDarkMuted">
              disabled — verify-mobile/verify-email pass this while the verify
              request is in flight, so a second tap can&apos;t double-submit
            </Text>
            <PSOtpInput value="4821" onChangeText={() => {}} disabled />
          </YStack>

          {/* autoFocus — deliberately NOT RN's native autoFocus (fires on
            mount, which on a pushed/presented screen races the routing
            animation and silently loses the keyboard). PSOtpInput instead
            focuses imperatively from a `useFocusEffect`, which runs once
            THIS PAGE settles as the focused screen — so navigating here
            pops the keyboard on this field the same way landing on
            verify-mobile/verify-email does, and it refocuses if you
            navigate away and back. Kept in its own field/section so it
            doesn't steal focus from the live demo above. */}
          <YStack gap="$xs">
            <Text fontFamily="$body" fontSize="$small" color="$textDarkMuted">
              autoFocus — both OTP screens set this; focuses via useFocusEffect
              (after the nav transition settles) rather than RN&apos;s
              mount-time autoFocus, which this screen itself is popping the
              keyboard for right now
            </Text>
            <PSOtpInput
              value={autoFocusOtp}
              onChangeText={setAutoFocusOtp}
              autoFocus
            />
          </YStack>
        </YStack>
      </ScrollView>
    </>
  );
}
